import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import { api } from '../services/api';
import type { VpnKeyDetail, VpnKeyConfigResponse } from '../services/types';
import { formatDate } from '../utils/format';
import { useAuth } from '../hooks/useAuth';

export function VpnKeyDetailPage() {
  const { keyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const id = Number(keyId);

  const [detail, setDetail] = useState<VpnKeyDetail | null>(null);
  const [config, setConfig] = useState<VpnKeyConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const canWrite = user?.role === 'admin' || user?.role === 'superadmin';

  const load = useCallback(async () => {
    if (!id || Number.isNaN(id)) return;
    setLoading(true);
    setError(null);
    const res = await api.getVpnKey(id);
    if (res.data) setDetail(res.data);
    else if (res.error) setError(res.error.message);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchConfig = async () => {
    if (!id) return;
    setBusy(true);
    setMessage(null);
    const res = await api.getVpnKeyConfig(id);
    if (res.data) {
      setConfig(res.data);
      toast.success('Конфиг загружен');
      setMessage('Конфиг загружен');
    } else if (res.error) {
      setError(res.error.message);
      toast.error(res.error.message);
    }
    setBusy(false);
  };

  const downloadConfig = () => {
    if (!config) return;
    const blob = new Blob([config.config_text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wg-${config.server_code}-${config.client_id.slice(0, 8)}.conf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteKey = async () => {
    if (!id || !window.confirm('Удалить ключ на сервере и в БД?')) return;
    setBusy(true);
    setError(null);
    const res = await api.deleteVpnKey(id);
    if (res.data?.deleted) {
      toast.success('Ключ удалён');
      navigate('/vpn');
    } else if (res.error) {
      setError(res.error.message);
      toast.error(res.error.message);
      setBusy(false);
    }
  };

  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>;
  if (error && !detail) return <p style={{ color: 'var(--danger)' }}>{error}</p>;
  if (!detail) return <p>Ключ не найден</p>;

  const subDay = detail.subscription_until
    ? new Date(detail.subscription_until).toISOString().slice(0, 10)
    : null;
  const wgDayRaw = detail.wg_live?.expires_at || null;
  const wgDay = wgDayRaw
    ? (wgDayRaw.length >= 10 ? wgDayRaw.slice(0, 10) : new Date(wgDayRaw).toISOString().slice(0, 10))
    : null;
  const subWgDesync = Boolean(subDay && wgDay && subDay !== wgDay);

  return (
    <div>
      <p style={{ marginTop: 0 }}>
        <Link to="/vpn">← VPN ключи</Link>
      </p>

      <h1 style={{ marginTop: 0 }}>Ключ #{detail.id}</h1>

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {message && <p style={{ color: 'var(--success)' }}>{message}</p>}
      {subWgDesync && (
        <p
          style={{
            color: 'var(--danger)',
            background: 'color-mix(in srgb, var(--danger) 12%, transparent)',
            padding: '0.75rem 1rem',
            borderRadius: 8,
          }}
        >
          Рассинхрон: подписка в БД до {subDay}, а WireGuard Expires {wgDay}. Срок в профиле
          пользователя — источник прав для портала; ключ на сервере мог остаться длиннее после сбоя
          начисления. Выдай дни в Finance → Grant или поправь subscription_until под WG.
        </p>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <dl className="detail-grid">
          <dt>User ID</dt>
          <dd>
            <Link to={`/users/${detail.user_id}`}>{detail.user_id}</Link>
          </dd>
          <dt>Пользователь</dt>
          <dd>{detail.first_name || detail.username || '—'}</dd>
          <dt>Сервер</dt>
          <dd>
            <code>{detail.server_country}</code>
          </dd>
          <dt>Client ID</dt>
          <dd>
            <code>{detail.client_id}</code>
          </dd>
          <dt>Статус</dt>
          <dd>
            <span className={detail.status === 'active' ? 'badge badge-success' : 'badge'}>
              {detail.status}
            </span>
          </dd>
          <dt>Создан</dt>
          <dd>{formatDate(detail.created_at)}</dd>
          <dt>Подписка до (users)</dt>
          <dd>
            {formatDate(detail.subscription_until)}
            {detail.subscription_tier ? ` · ${detail.subscription_tier}` : ''}
          </dd>
        </dl>
      </div>

      {detail.wg_live && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>WireGuard (live)</h3>
          {detail.wg_live.reachable ? (
            <dl className="detail-grid">
              <dt>Enabled</dt>
              <dd>{detail.wg_live.enabled ? 'да' : 'нет'}</dd>
              <dt>Имя</dt>
              <dd>{detail.wg_live.name || '—'}</dd>
              <dt>Expires</dt>
              <dd>{detail.wg_live.expires_at || '—'}</dd>
            </dl>
          ) : (
            <p style={{ color: 'var(--text-muted)' }}>
              Недоступен: {detail.wg_live.error || 'ошибка'}
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {detail.status === 'active' && (
          <>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={fetchConfig}>
              Загрузить конфиг
            </button>
            {config && (
              <button type="button" className="btn btn-ghost" onClick={downloadConfig}>
                Скачать .conf
              </button>
            )}
            {canWrite && (
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={deleteKey}>
                Удалить ключ
              </button>
            )}
          </>
        )}
      </div>

      {config && (
        <pre
          className="card"
          style={{
            marginTop: '1rem',
            overflow: 'auto',
            fontSize: '0.75rem',
            maxHeight: 320,
          }}
        >
          {config.config_text}
        </pre>
      )}
    </div>
  );
}
